import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInput, MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { L10N_LOCALE, L10nLocale, L10nTranslatePipe } from 'angular-l10n';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  finalize,
  of,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { EllipsisPipe } from 'src/app/shared/ellipsis/ellipsis.pipe';
import { FilterValueType, FilterValues, FilterVariable } from '../../filtered-search.component';

@Component({
  selector: 'dynamic-selection-filter',
  templateUrl: './dynamic-selection-filter.component.html',
  styleUrls: ['../filtered-search-option.component.scss', './dynamic-selection-filter.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    ReactiveFormsModule,
    L10nTranslatePipe,
    EllipsisPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicSelectionFilterComponent implements OnInit, OnChanges, OnDestroy {
  readonly locale = inject<L10nLocale>(L10N_LOCALE);

  @ViewChild(MatChipInput) input: MatChipInput | undefined;
  @ViewChild('auto') autocomplete: MatAutocomplete | undefined;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger | undefined;
  @Input() variables: FilterVariable[] | undefined;
  @Input() control: FormControl<FilterValueType> | undefined;
  @Input() func: (input: FilterValueType) => Observable<FilterValues> | undefined = () => undefined;

  inputControl = new FormControl('');
  selectedChips = signal<string[]>([]);
  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredOptions = computed(() =>
    this.unfilteredOptions().filter((option) => !this.selectedChips().includes(option?.trim())),
  );

  unfilteredOptions = signal<string[]>([]);
  destroy$ = new Subject<void>();

  loading = signal(false);
  safeFunc = (input: FilterValueType) => this.func(input)?.pipe(catchError(() => of([]))) ?? of([]);

  ngOnInit() {
    this.subscribeToInputControl();
    this.subscribeToControl();

    this.selectedChips.set(this.cleanValues(this.control?.value as string));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.control) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.selectedChips.set(this.cleanValues(changes.control?.currentValue?.value as string));

      this.loading.set(true);
      this.safeFunc('')
        .pipe(
          tap((values) => this.unfilteredOptions.set(values as string[])),
          finalize(() => this.loading.set(false)),
        )
        .subscribe();

      this.controlSubscription?.unsubscribe();
      this.subscribeToControl();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private controlSubscription: Subscription | undefined;
  subscribeToControl() {
    this.controlSubscription = this.control?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .pipe(
        tap((value) => {
          this.selectedChips.set(this.cleanValues(value as string));
        }),
      )
      .subscribe();
  }

  subscribeToInputControl() {
    this.inputControl.valueChanges
      .pipe(
        debounceTime(300),
        startWith(''),
        switchMap((value) => {
          const filterValue = typeof value === 'string' ? value : '';
          this.loading.set(true);
          return this.safeFunc(filterValue).pipe(finalize(() => this.loading.set(false)));
        }),
        tap((options) => {
          this.unfilteredOptions.set(options as string[]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  getValue(chip: string): string {
    if (this.isVariable(chip)) {
      return this.variables?.find((variable) => variable.value === chip)?.label ?? chip;
    }

    return chip.trim();
  }

  isVariable(chip: string): boolean {
    return this.variables?.some((variable) => variable.value === chip) ?? false;
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value === null) {
      return;
    }

    this.inputControl.setValue('');
    this.selectedChips.update((chips) => [...chips, event.option.viewValue]);
    if (this.input) {
      this.input.inputElement.value = '';
    }
    this.updateMainControl();
  }

  addChip(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (this.selectedChips()?.includes(value)) {
      event.chipInput.clear();
      this.inputControl.setValue('');
      return;
    }

    if (value) {
      this.selectedChips.update((chips) => [...chips, value]);
      this.updateMainControl();
    }

    event.chipInput.clear();
    this.inputControl.setValue('');
  }

  removeChip(chip: string): void {
    const index = this.selectedChips()?.indexOf(chip);

    if (index >= 0) {
      this.selectedChips.update((chips) => chips.filter((_, i) => i !== index));
      this.updateMainControl();
    }
  }

  private updateMainControl(): void {
    this.control?.setValue(this.selectedChips().join('||'));
  }

  private cleanValues(value: string): string[] {
    return (
      value
        ?.split('||')
        .map((val) => val.trim())
        .filter((val) => val) ?? []
    );
  }

  onInputBlur(): void {
    setTimeout(() => {
      if (!this.autocomplete?.isOpen) {
        this.inputControl.setValue('');
      }
      this.autocompleteTrigger?.closePanel();
    }, 150);
  }

  onAutocompleteClosed(): void {
    this.inputControl.setValue('');
  }
}
